/**
 * Calculation Expression Builder for Custom Field Definitions.
 *
 * Provides a visual editor for the `calculationExpression` column on custom field definitions.
 * Supports NCalc expressions including child aggregation, parent references, payload lookups,
 * and date calculations. See docs/product/product-architecture.md §6.6 for the full spec.
 */
import { useCallback, useMemo, useState } from "react";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";

// =============================================================================
// TYPES
// =============================================================================

interface CalculationExpressionBuilderProps {
    /** The current expression string */
    value: string;
    /** Called with the updated expression string */
    onChange: (expression: string) => void;
    isDisabled?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const FUNCTION_CATEGORIES: SelectItemType[] = [
    { id: "custom", label: "Custom Expression" },
    { id: "SUM_CHILD", label: "SUM_CHILD — Sum child field values" },
    { id: "AVG_CHILD", label: "AVG_CHILD — Average child field values" },
    { id: "MIN_CHILD", label: "MIN_CHILD — Min child field value" },
    { id: "MAX_CHILD", label: "MAX_CHILD — Max child field value" },
    { id: "COUNT_CHILD", label: "COUNT_CHILD — Count child documents" },
    { id: "PARENT", label: "PARENT — Reference parent field" },
    { id: "PAYLOAD_LOOKUP", label: "PAYLOAD_LOOKUP — JSON payload by key" },
    { id: "PAYLOAD_INDEX", label: "PAYLOAD_INDEX — JSON payload by index" },
    { id: "TERM_DATE", label: "TERM_DATE — Date calculation" },
];

const TERM_DATE_MODES: SelectItemType[] = [
    { id: "D", label: "D — Days from base date" },
    { id: "M", label: "M — Month-end offset" },
    { id: "F", label: "F — Fixed day of month" },
];

/** Detect which function category matches the current expression */
function detectCategory(expression: string): string {
    const trimmed = expression.trim();
    if (!trimmed) return "custom";
    if (trimmed.startsWith("SUM_CHILD(")) return "SUM_CHILD";
    if (trimmed.startsWith("AVG_CHILD(")) return "AVG_CHILD";
    if (trimmed.startsWith("MIN_CHILD(")) return "MIN_CHILD";
    if (trimmed.startsWith("MAX_CHILD(")) return "MAX_CHILD";
    if (trimmed.startsWith("COUNT_CHILD(")) return "COUNT_CHILD";
    if (trimmed.startsWith("PARENT(")) return "PARENT";
    if (trimmed.startsWith("PAYLOAD_LOOKUP(")) return "PAYLOAD_LOOKUP";
    if (trimmed.startsWith("PAYLOAD_INDEX(")) return "PAYLOAD_INDEX";
    if (trimmed.startsWith("TERM_DATE(")) return "TERM_DATE";
    return "custom";
}

/** Parse arguments from a function call like FN('arg1', 'arg2') */
function parseArgs(expression: string, fnName: string): string[] {
    const prefix = fnName + "(";
    if (!expression.startsWith(prefix)) return [];
    const inner = expression.slice(prefix.length, expression.lastIndexOf(")"));
    // Split by comma, strip quotes and whitespace
    return inner.split(",").map((arg) => arg.trim().replace(/^'|'$/g, ""));
}

/** Build a function call from name and args */
function buildCall(fnName: string, args: string[]): string {
    const quoted = args.map((a) => `'${a}'`);
    return `${fnName}(${quoted.join(", ")})`;
}

// =============================================================================
// CHILD AGGREGATION BUILDER (SUM_CHILD, AVG_CHILD, etc.)
// =============================================================================

function ChildAggregationBuilder({
    fnName,
    value,
    onChange,
    isDisabled,
}: {
    fnName: string;
    value: string;
    onChange: (v: string) => void;
    isDisabled?: boolean;
}) {
    const args = useMemo(() => parseArgs(value, fnName), [value, fnName]);
    const childType = args[0] ?? "";
    const field = args[1] ?? "";

    const update = useCallback(
        (idx: number, v: string) => {
            const newArgs = [childType, field];
            newArgs[idx] = v;
            onChange(buildCall(fnName, newArgs));
        },
        [fnName, childType, field, onChange],
    );

    return (
        <div className="grid grid-cols-2 gap-3">
            <Input
                label="Child Type (document/line type code)"
                value={childType}
                onChange={(v) => update(0, v)}
                size="sm"
                isDisabled={isDisabled}
                placeholder="e.g. InvoiceLine"
            />
            <Input label="Field Code" value={field} onChange={(v) => update(1, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. ExtendedAmount" />
        </div>
    );
}

// =============================================================================
// PARENT BUILDER
// =============================================================================

function ParentBuilder({ value, onChange, isDisabled }: { value: string; onChange: (v: string) => void; isDisabled?: boolean }) {
    const args = useMemo(() => parseArgs(value, "PARENT"), [value]);
    const field = args[0] ?? "";

    return (
        <Input
            label="Parent Field Code"
            value={field}
            onChange={(v) => onChange(buildCall("PARENT", [v]))}
            size="sm"
            isDisabled={isDisabled}
            placeholder="e.g. TotalAmount"
        />
    );
}

// =============================================================================
// PAYLOAD LOOKUP BUILDER
// =============================================================================

function PayloadLookupBuilder({ value, onChange, isDisabled }: { value: string; onChange: (v: string) => void; isDisabled?: boolean }) {
    const args = useMemo(() => parseArgs(value, "PAYLOAD_LOOKUP"), [value]);
    const collection = args[0] ?? "";
    const key = args[1] ?? "";
    const docField = args[2] ?? "";
    const path = args[3] ?? "";

    const update = useCallback(
        (idx: number, v: string) => {
            const newArgs = [collection, key, docField, path];
            newArgs[idx] = v;
            onChange(buildCall("PAYLOAD_LOOKUP", newArgs));
        },
        [collection, key, docField, path, onChange],
    );

    return (
        <div className="grid grid-cols-2 gap-3">
            <Input label="Collection" value={collection} onChange={(v) => update(0, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. vendors" />
            <Input label="Key Field" value={key} onChange={(v) => update(1, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. vendor_id" />
            <Input label="Document Field" value={docField} onChange={(v) => update(2, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. VendorNumber" />
            <Input label="JSON Path" value={path} onChange={(v) => update(3, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. $.paymentTerms" />
        </div>
    );
}

// =============================================================================
// PAYLOAD INDEX BUILDER
// =============================================================================

function PayloadIndexBuilder({ value, onChange, isDisabled }: { value: string; onChange: (v: string) => void; isDisabled?: boolean }) {
    const args = useMemo(() => parseArgs(value, "PAYLOAD_INDEX"), [value]);
    const collection = args[0] ?? "";
    const indexField = args[1] ?? "";
    const path = args[2] ?? "";

    const update = useCallback(
        (idx: number, v: string) => {
            const newArgs = [collection, indexField, path];
            newArgs[idx] = v;
            onChange(buildCall("PAYLOAD_INDEX", newArgs));
        },
        [collection, indexField, path, onChange],
    );

    return (
        <div className="grid grid-cols-2 gap-3">
            <Input label="Collection" value={collection} onChange={(v) => update(0, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. receipts" />
            <Input label="Index Field" value={indexField} onChange={(v) => update(1, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. LineNumber" />
            <Input label="JSON Path" value={path} onChange={(v) => update(2, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. $.unitCost" />
        </div>
    );
}

// =============================================================================
// TERM DATE BUILDER
// =============================================================================

function TermDateBuilder({ value, onChange, isDisabled }: { value: string; onChange: (v: string) => void; isDisabled?: boolean }) {
    const args = useMemo(() => parseArgs(value, "TERM_DATE"), [value]);
    const baseField = args[0] ?? "";
    const mode = args[1] ?? "D";
    const interval = args[2] ?? "";

    const update = useCallback(
        (idx: number, v: string) => {
            const newArgs = [baseField, mode, interval];
            newArgs[idx] = v;
            onChange(buildCall("TERM_DATE", newArgs));
        },
        [baseField, mode, interval, onChange],
    );

    return (
        <div className="grid grid-cols-3 gap-3">
            <Input label="Base Date Field" value={baseField} onChange={(v) => update(0, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. InvoiceDate" />
            <Select
                label="Mode"
                items={TERM_DATE_MODES}
                selectedKey={mode}
                onSelectionChange={(key) => update(1, String(key))}
                size="sm"
                isDisabled={isDisabled}
            >
                {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
            </Select>
            <Input label="Interval" value={interval} onChange={(v) => update(2, v)} size="sm" isDisabled={isDisabled} placeholder="e.g. 30" type="number" />
        </div>
    );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function CalculationExpressionBuilder({ value, onChange, isDisabled }: CalculationExpressionBuilderProps) {
    const [rawMode, setRawMode] = useState(false);
    const detectedCategory = useMemo(() => detectCategory(value), [value]);
    const [category, setCategory] = useState(detectedCategory);

    // Sync category when value changes externally (e.g. load from API)
    const prevDetected = useMemo(() => detectedCategory, [detectedCategory]);
    if (prevDetected !== category && prevDetected !== "custom") {
        // Only auto-sync if switching to a known function; keep "custom" if user chose it
    }

    const handleCategoryChange = useCallback(
        (key: string | number | null) => {
            const cat = String(key);
            setCategory(cat);
            if (cat === "custom") {
                // Keep current value, user will edit raw
            } else if (["SUM_CHILD", "AVG_CHILD", "MIN_CHILD", "MAX_CHILD", "COUNT_CHILD"].includes(cat)) {
                onChange(buildCall(cat, ["", ""]));
            } else if (cat === "PARENT") {
                onChange(buildCall("PARENT", [""]));
            } else if (cat === "PAYLOAD_LOOKUP") {
                onChange(buildCall("PAYLOAD_LOOKUP", ["", "", "", ""]));
            } else if (cat === "PAYLOAD_INDEX") {
                onChange(buildCall("PAYLOAD_INDEX", ["", "", ""]));
            } else if (cat === "TERM_DATE") {
                onChange(buildCall("TERM_DATE", ["", "D", ""]));
            }
        },
        [onChange],
    );

    const isChildAgg = ["SUM_CHILD", "AVG_CHILD", "MIN_CHILD", "MAX_CHILD", "COUNT_CHILD"].includes(category);

    return (
        <div className="space-y-3">
            {/* Header with raw toggle */}
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-secondary">Calculation Expression</label>
                <button
                    type="button"
                    className="cursor-pointer text-xs font-medium text-brand-tertiary transition-colors hover:text-brand-primary"
                    onClick={() => setRawMode(!rawMode)}
                >
                    {rawMode ? "Visual Builder" : "Raw Expression"}
                </button>
            </div>

            {rawMode ? (
                <TextArea
                    value={value}
                    onChange={onChange}
                    isDisabled={isDisabled}
                    placeholder="NCalc expression, e.g. SUM_CHILD('InvoiceLine', 'ExtendedAmount')"
                    rows={3}
                    textAreaClassName="font-mono text-xs"
                />
            ) : (
                <div className="space-y-3 rounded-lg border border-secondary p-3">
                    {/* Function type selector */}
                    <Select
                        label="Function Type"
                        items={FUNCTION_CATEGORIES}
                        selectedKey={category}
                        onSelectionChange={handleCategoryChange}
                        size="sm"
                        isDisabled={isDisabled}
                    >
                        {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                    </Select>

                    {/* Type-specific builders */}
                    {isChildAgg && <ChildAggregationBuilder fnName={category} value={value} onChange={onChange} isDisabled={isDisabled} />}

                    {category === "PARENT" && <ParentBuilder value={value} onChange={onChange} isDisabled={isDisabled} />}

                    {category === "PAYLOAD_LOOKUP" && <PayloadLookupBuilder value={value} onChange={onChange} isDisabled={isDisabled} />}

                    {category === "PAYLOAD_INDEX" && <PayloadIndexBuilder value={value} onChange={onChange} isDisabled={isDisabled} />}

                    {category === "TERM_DATE" && <TermDateBuilder value={value} onChange={onChange} isDisabled={isDisabled} />}

                    {category === "custom" && (
                        <Input
                            label="Expression"
                            value={value}
                            onChange={onChange}
                            size="sm"
                            isDisabled={isDisabled}
                            placeholder="e.g. [Amount] * 1.1 or any NCalc expression"
                        />
                    )}

                    {/* Preview */}
                    {value && (
                        <div className="rounded bg-secondary_subtle px-3 py-2">
                            <p className="text-[10px] font-medium text-quaternary">Result</p>
                            <code className="font-mono text-xs text-primary">{value}</code>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
